import React, { useMemo, useState } from "react";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import { SearchBar } from "components/common/SearchBar";
import { SectionRule } from "components/common/SectionRule";
import { Callout } from "components/common/Callout";
import { Dialog } from "@/components/main/utils/Dialog";
import { DialogActions } from "@/components/main/utils/DialogActions";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { type EmailChip } from "components/shareSection/EmailChipInput";
import { ShareComposer } from "components/shareSection/EmailSection";
import { AccessItem, PeopleScroll } from "components/shareSection/AccessItem";
import { RoleOption, RowMenu } from "components/shareSection/PermissionControl";
import { RefreshableCount } from "@/components/common/RefreshableCount";
import { SeatLimitNotice } from "components/main/organizations/SeatLimitNotice";
import { InlineSpinner } from "components/common/InlineSpinner";
import {
  selectReadOnly,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import {
  addOrgMember,
  fetchRemoveMemberPreview,
  removeOrgMember,
  SeatLimitError,
  setOrgAdmin,
  setOrgOwner,
  type LeaveOrgPreview,
  type Org,
  type OrgMember,
} from "@/lib/api/organizations";

const message = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

/// After a seat purchase the invite door still reads the old cap until the
/// webhook lands, so the refused invitation is retried for a while.
const SEAT_SETTLE_ATTEMPTS = 6;
const SEAT_SETTLE_INTERVAL_MS = 1500;

const roleLabel = (role: string) =>
  role === "org_admin" ? "Admin" : role === "owner" ? "Owner" : "Member";

// Owner is a set, not a slot: an org may hold several, and the server refuses
// to remove the last one, so demotion stays offered.
const ORG_ROLES: RoleOption[] = [
  { label: "Member" },
  { label: "Admin", note: "governs, cannot read" },
  { label: "Owner", note: "full control" },
];
const ROLE_OF_LABEL: Record<string, OrgRole> = {
  Member: "member",
  Admin: "org_admin",
  Owner: "owner",
};

export const MembersPanel: React.FC<{
  org: Org;
  members: OrgMember[];
  onChanged: () => void | Promise<void>;
}> = ({ org, members, onChanged }) => {
  const [inviteChips, setInviteChips] = useState<EmailChip[]>([]);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState(false);
  /// The seat-cap refusal, with the address to re-invite once a seat exists.
  const [seatWall, setSeatWall] = useState<{
    used: number;
    limit: number;
    email: string;
  } | null>(null);
  /// The address whose invitation is being retried after a seat purchase.
  const [settlingSeat, setSettlingSeat] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const searchShown = members.length > 8;
  // A scheduled org takes no new members: the server refuses.
  const scheduledForDeletion = !!org.deletion_scheduled_at;
  // Nor does a read-only one; the banner above says why.
  const readOnly = useOrgContextStore((s) => selectReadOnly(s, org.org_id));

  const pendingCount = useMemo(
    () => members.filter((m) => m.pending).length,
    [members],
  );
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return members;
    return members.filter(
      (m) =>
        m.display_name.toLowerCase().includes(needle) ||
        m.email.toLowerCase().includes(needle),
    );
  }, [members, search]);

  const confirmInvited = () => {
    setInvited(true);
    setTimeout(() => setInvited(false), 3000);
  };

  /// True once sent. `settleAttempts` retries a seat-cap refusal while the
  /// webhook lands.
  const sendInvite = async (
    email: string,
    settleAttempts = 0,
  ): Promise<boolean> => {
    try {
      await addOrgMember(org.org_id, email);
      setInviteChips((now) => now.filter((chip) => chip.email !== email));
      setSeatWall(null);
      setSettlingSeat(null);
      onChanged();
      return true;
    } catch (error) {
      if (error instanceof SeatLimitError) {
        if (settleAttempts > 0) {
          await new Promise((resolve) =>
            setTimeout(resolve, SEAT_SETTLE_INTERVAL_MS),
          );
          return sendInvite(email, settleAttempts - 1);
        }
        setSettlingSeat(null);
        setSeatWall({ used: error.used, limit: error.limit, email });
        return false;
      }
      setSettlingSeat(null);
      setInviteError(message(error, "Invite failed"));
      return false;
    }
  };

  // One at a time, stopping at the first refusal: a full plan refuses the rest too.
  const submitInvites = async () => {
    const emails = inviteChips
      .filter((chip) => chip.status === "valid")
      .map((chip) => chip.email);
    if (emails.length === 0 || inviting) return;
    setInviteError(null);
    setSeatWall(null);
    setInviting(true);
    let sent = 0;
    for (const email of emails) {
      if (!(await sendInvite(email))) break;
      sent += 1;
    }
    setInviting(false);
    if (sent === emails.length) confirmInvited();
  };

  /// Owner is a separate door from admin, and moving between the two means
  /// clearing the one you are leaving.
  const changeRole = async (member: OrgMember, next: OrgRole) => {
    try {
      if (member.role === "owner" && next !== "owner") {
        await setOrgOwner(org.org_id, member.client_id, false);
      }
      if (next === "owner") {
        await setOrgOwner(org.org_id, member.client_id, true);
      } else {
        await setOrgAdmin(org.org_id, member.client_id, next === "org_admin");
      }
      onChanged();
      showNotificationGlobal(
        `${member.display_name || member.email} is now ${roleLabel(next)}`,
        "success",
      );
    } catch (error) {
      showNotificationGlobal(message(error, "Role change failed"), "error");
    }
  };

  // Removal can reap the member's empty spaces and revoke their grants, so
  // the confirm names both before it happens (D43).
  const [removing, setRemoving] = useState<OrgMember | null>(null);
  /// An owner is removed by an owner, while another owner remains.
  const ownerCount = members.filter(
    (m) => m.role === "owner" && !m.pending,
  ).length;
  // Roles are `can_manage_admins`, owner-only; an admin manages members, not roles.
  const roleChangeBlock =
    org.role === "owner" ? null : "Only an owner can change roles";
  const removeBlockFor = (member: OrgMember): string | null => {
    if (member.role !== "owner") return null;
    if (ownerCount <= 1) return "Transfer ownership first";
    if (org.role !== "owner") return "Only an owner can remove an owner";
    return null;
  };

  const [removePreview, setRemovePreview] = useState<LeaveOrgPreview | null>(
    null,
  );

  const openRemove = async (member: OrgMember) => {
    setRemovePreview(null);
    setRemoving(member);
    try {
      setRemovePreview(
        await fetchRemoveMemberPreview(org.org_id, member.client_id),
      );
    } catch (error) {
      setRemoving(null);
      showNotificationGlobal(message(error, "Could not check"), "error");
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    const label = removing.display_name || removing.email;
    try {
      await removeOrgMember(org.org_id, removing.client_id);
      setRemoving(null);
      onChanged();
      showNotificationGlobal(`Removed ${label}`, "success");
    } catch (error) {
      showNotificationGlobal(message(error, "Removal failed"), "error");
    }
  };

  const cancelInvite = async (member: OrgMember) => {
    try {
      await removeOrgMember(org.org_id, member.client_id, true);
      await onChanged();
      showNotificationGlobal(
        `Invitation to ${member.email} cancelled`,
        "success",
      );
    } catch (error) {
      // Most likely they accepted while this roster sat open, so show where
      // they actually stand rather than leaving the stale row on screen.
      await onChanged();
      showNotificationGlobal(message(error, "Could not cancel"), "error");
    }
  };

  return (
    <>
      {/* Laid out as a space's Access: rule and count, composer, then the people. */}
      <section className="flex flex-col">
        <SectionRule
          label="Members"
          trailing={
            <RefreshableCount
              label={`${members.length}${
                pendingCount > 0 ? ` · ${pendingCount} pending` : ""
              }`}
              subject="members"
              onRefresh={onChanged}
            />
          }
        />
        <div className="flex flex-col gap-2">
          {scheduledForDeletion && (
            <Callout variant="info">
              This organization is scheduled for deletion and takes no new
              members. Cancel the deletion in General first.
            </Callout>
          )}
          <ShareComposer
            chips={inviteChips}
            onChipsChange={setInviteChips}
            chipInput={{}}
            onShare={() => void submitInvites()}
            sharing={inviting}
            shared={invited}
            disabled={scheduledForDeletion || readOnly}
            error={inviteError}
            onErrorChange={setInviteError}
            sharedMessage="Invitations sent"
            onEdit={() => {
              setInvited(false);
              setSeatWall(null);
            }}
            verb={{ act: "Invite", done: "Invited" }}
          />
          {seatWall && (
            <SeatLimitNotice
              orgId={org.org_id}
              used={seatWall.used}
              limit={seatWall.limit}
              email={seatWall.email}
              onSeatsAdded={() => {
                const { email } = seatWall;
                setSeatWall(null);
                setSettlingSeat(email);
                return sendInvite(email, SEAT_SETTLE_ATTEMPTS).then((sent) => {
                  if (sent) confirmInvited();
                });
              }}
            />
          )}
          {settlingSeat && (
            <span
              className="flex items-center gap-2 px-2 text-fg-muted-regular-small"
              data-testid="seat-settling"
            >
              <InlineSpinner size={14} />
              {`Seats added. Sending the invitation to ${settlingSeat}…`}
            </span>
          )}
          {searchShown && (
            // The list pages' search; its icon lines up with the names below.
            <SearchBar
              size="lg"
              className="mt-2"
              value={search}
              onChange={setSearch}
              placeholder="Search people"
              inputProps={{ "aria-label": "Search members" }}
              onKeyDown={(e) => {
                if (e.key !== "Escape" || !search) return;
                e.preventDefault();
                setSearch("");
              }}
            />
          )}
          <div className={searchShown ? undefined : "mt-2"}>
            <PeopleScroll fill>
              <div className="space-y-1">
                {visible.map((member) => (
                  <MemberRow
                    key={member.client_id}
                    member={member}
                    roleChangeBlock={roleChangeBlock}
                    removeBlock={removeBlockFor(member)}
                    onRoleChange={(next) => void changeRole(member, next)}
                    onRemove={() =>
                      member.pending
                        ? void cancelInvite(member)
                        : void openRemove(member)
                    }
                  />
                ))}
                {visible.length === 0 && (
                  <span className="px-2 py-2 text-fg-muted-regular-small">
                    {search ? "No members match." : "No members yet."}
                  </span>
                )}
              </div>
            </PeopleScroll>
          </div>
        </div>
      </section>

      <Dialog
        open={removing !== null}
        title={`Remove ${(removing?.display_name || removing?.email) ?? ""}?`}
        onClose={() => setRemoving(null)}
      >
        {removePreview === null ? (
          <span className="text-fg-muted-regular">
            Checking the effects of removal…
          </span>
        ) : (
          <div className="flex flex-col gap-2">
            {removePreview.is_owner_blocked ? (
              <span className="text-fg-regular">
                They are this organization&apos;s only owner. Make someone else
                an owner first.
              </span>
            ) : (
              <>
                <span className="text-fg-regular">
                  They will lose access to all content in this organization.
                </span>
                {removePreview.pays_for_org && (
                  <Callout
                    variant="warning"
                    title="This organization's subscription is on their card"
                  >
                    Removing them does not move it: it keeps charging them until
                    you replace the card in Plan &amp; usage.
                  </Callout>
                )}
                {removePreview.spaces_to_orphan.length > 0 && (
                  <Callout
                    variant="warning"
                    title="These spaces lose their manager"
                  >
                    <span className="flex flex-col gap-1">
                      <span className="text-fg-medium">
                        {removePreview.spaces_to_orphan.join(", ")}
                      </span>
                      They are the only manager. Nobody will own the models
                      inside until someone is appointed — Spaces lists them as
                      orphaned.
                    </span>
                  </Callout>
                )}
                {removePreview.spaces_to_delete.length > 0 && (
                  <span className="text-fg-regular">
                    Their empty{" "}
                    {removePreview.spaces_to_delete.length === 1
                      ? "space"
                      : "spaces"}{" "}
                    <span className="text-fg-medium">
                      {removePreview.spaces_to_delete.join(", ")}
                    </span>{" "}
                    will be deleted.
                  </span>
                )}
                {removePreview.grants_to_revoke.length > 0 && (
                  <span className="text-fg-regular">
                    Their access to{" "}
                    <span className="text-fg-medium">
                      {removePreview.grants_to_revoke.join(", ")}
                    </span>{" "}
                    will be revoked; other members keep theirs.
                  </span>
                )}
              </>
            )}
          </div>
        )}
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setRemoving(null),
            },
            {
              label: "Remove from organization",
              variant: "primary",
              disabled:
                removePreview === null || removePreview.is_owner_blocked,
              onClick: () => void confirmRemove(),
            },
          ]}
        />
      </Dialog>
    </>
  );
};

type OrgRole = "member" | "org_admin" | "owner";

const MemberRow: React.FC<{
  member: OrgMember;
  /// Why this viewer cannot change roles, or `null` when they can.
  roleChangeBlock: string | null;
  /// Why this member cannot be removed from here, or `null` when they can.
  removeBlock: string | null;
  onRoleChange: (next: OrgRole) => void;
  onRemove: () => void;
}> = ({ member, roleChangeBlock, removeBlock, onRoleChange, onRemove }) => {
  const [active, setActive] = useState(false);
  const who = member.display_name || member.email;
  const role = roleLabel(member.role);
  const control = member.pending ? (
    <RowMenu
      label="Invited"
      ariaLabel={`Invited, actions for ${member.email}`}
      menuLabel={`Invitation for ${member.email}`}
      actions={[
        {
          Icon: PersonRemoveOutlinedIcon,
          label: "Cancel Invite",
          onClick: onRemove,
          destructive: true,
        },
      ]}
      onMenuToggle={setActive}
    />
  ) : (
    <RowMenu
      label={role}
      ariaLabel={`${role}, access for ${who}`}
      menuLabel={`Access for ${who}`}
      roles={ORG_ROLES.map((option) => ({
        ...option,
        locked: roleChangeBlock ?? undefined,
      }))}
      current={role}
      onPick={(picked) => onRoleChange(ROLE_OF_LABEL[picked])}
      actions={[
        {
          Icon: PersonRemoveOutlinedIcon,
          label: "Remove from organization",
          onClick: onRemove,
          destructive: true,
          locked: removeBlock ?? undefined,
        },
      ]}
      onMenuToggle={setActive}
    />
  );
  return (
    <AccessItem
      primaryText={who}
      isPending={member.pending}
      isCurrentUser={member.client_id === localStorage.getItem("clientID")}
      email={member.display_name && member.email ? member.email : undefined}
      isActive={active}
      permissionLabel={control}
    />
  );
};
